import { SimpleSearchFilter } from 'module/api/filter/types';
import { useTranslation } from 'module/hooks/use-translation';
import { logger } from 'module/logger';
import {
  defaultGroupedFilter,
  GroupedFilter,
} from 'module/types/filter-field/grouped-filter/filter';
import { FilterGroupType } from 'module/types/filter-field/grouped-filter/group';
import { GroupFieldsMeta } from 'module/types/filter-field/transformer/filter-transformer';
import { Breadcrumb } from 'module/ui/components/atoms/breadcrumb/breadcrumb';
import { Dropdown } from 'module/ui/components/atoms/dropdown/dropdown';
import { FontAwesomeIcon } from 'module/ui/components/atoms/icon/icon';
import { PageLoading } from 'module/ui/components/atoms/loading';
import { Popover } from 'module/ui/components/atoms/popover';
import { getApiGroupedFilterTransformer } from 'module/ui/components/molecules/field-filter/filter-transformation/api-grouped-filter-transformer';
import { Argon } from 'module/ui/components/templates/argon';
import {
  faChevronDown,
  faPlay,
  faFloppyDisk,
} from '@fortawesome/pro-solid-svg-icons';
import { Link, useParams, useNavigate } from '@rocketlane/rocket-router';
import { Button } from '@rocketlane/ui';
import { Menu, Tooltip } from 'antd';
import { Email } from 'api/email';
import { EmailPageTemplateUiState } from 'api/email-page-template/types';
import {
  PageType,
  SourceType,
  OperationPayload,
} from 'api/page-template/types';
import { useCompanyFieldsAsFilterableField } from 'helpers/fields/company-fields/fields';
import { useEmailFieldsAsFilterableField } from 'helpers/fields/email-fields/fields';
import { useExternalTeamMemberFieldsAsFilterableField } from 'helpers/fields/external-team-members-fields/fields';
import { useMeetingFieldsAsFilterableField } from 'helpers/fields/meeting-fields/fields';
import { keyBy } from 'lodash';
import {
  useCreateEmailPageTemplateMutation,
  useGetEmailPageTemplateQuery,
  useUpdateEmailPageTemplateMutation,
} from 'queries/email-page-templare';
import { useSelectedEmailId } from 'queries/emails';
import { useState, useCallback } from 'react';
import { useEffect } from 'react';
import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { ROUTE_PATHS } from 'routes/route-paths';
import styled from 'styled-components';
import { FlexWrapperWithGutter } from 'components/common/styles';
import { PreviewButton } from 'components/organisms/document-editor/extensions/custom-section/ui/shared/section-styles';
import { EmailTemplatePage } from 'components/organisms/pages-template/email-page/email-page-template';
import {
  defaultSections,
  defaultContent,
  DEFAULT_SECTION_IDS,
} from 'components/organisms/pages-template/email-page/email-utils';
import { EmailList } from 'components/pages/email-page-template/preview/email-list-dropdown';
import { EmailPageLevelPreview } from 'components/pages/email-page-template/preview/email-page-preview';
import { MeetingDocumentHeaderTitle } from './template-title';

const EMAIL_PAGE_ROUTE = 'email-templates';

export type EmailTemplatePermissions = {
  canRead: boolean;
  canCreate: boolean;
  canWrite: boolean;
  canDelete: boolean;
};

const PreviewDropdownMenu = styled(Menu)`
  padding: var(--spacing-06);
  width: 510px;
  max-width: 510px;
  min-width: 0;
  overflow: hidden;
`;

const PreviewPopoverContent = styled.div`
  width: 476px;
  display: flex;
  flex-direction: column;
`;

const PreviewPopoverHeader = styled.div`
  padding: var(--spacing-05) var(--spacing-06);
  font-weight: var(--font-weight-semi-bold);
  font-size: var(--font-size-xl);
  border-bottom: 1px solid var(--divider-default);
`;

const PreviewPopoverTitle = styled.div`
  font-size: var(--font-size-04);
  font-weight: var(--font-weight-semi-bold);
  color: var(--text-primary);
`;

const PreviewPopoverBody = styled.div`
  padding: var(--spacing-06);
  display: flex;
  flex-direction: column;
`;

const PreviewPopoverLabel = styled.div`
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semi-bold);
  color: var(--text-primary);
  margin-bottom: var(--spacing-01);
`;

const PreviewPopoverFooter = styled.div`
  padding: var(--spacing-05) var(--spacing-06);
`;

const PreviewSelectTrigger = styled.div`
  border-radius: var(--border-radius-small);
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--spacing-03) var(--spacing-04);
  height: 32px;
  gap: var(--spacing-02);
  border: 1px solid var(--scarlet-gray-40);
  color: var(--text-primary);
  cursor: pointer;
  background: var(--background-primary);

  &:hover {
    border-color: var(--scarlet-gray-50);
  }
`;

const PreviewSelectTriggerEllipsisText = styled.div`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: var(--text-primary);
`;

export const EmailPageTemplatePage = ({
  emailTemplatePermissions,
  isModuleFed = true,
}: {
  emailTemplatePermissions?: EmailTemplatePermissions;
  isModuleFed?: boolean;
} = {}) => {
  const { loading: isLoadingPeopleFields } =
    useExternalTeamMemberFieldsAsFilterableField();
  const { fields: companyFields, loading: isLoadingCompanyFields } =
    useCompanyFieldsAsFilterableField();
  const { fields: meetingFields, loading: isLoadingMeetingFields } =
    useMeetingFieldsAsFilterableField();
  const { fields: emailFields, loading: isLoadingEmailFields } =
    useEmailFieldsAsFilterableField();
  const isLoadingFields =
    isLoadingCompanyFields ||
    isLoadingMeetingFields ||
    isLoadingPeopleFields ||
    isLoadingEmailFields;
  const meetingPageTemplateTitle = useTranslation('meeting_page_template');
  const { emailPageTemplateId } = useParams<{
    emailPageTemplateId: string;
  }>();
  const navigate = useNavigate();

  const [componentKey, setComponentKey] = useState(0);

  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isPreviewPopoverOpen, setIsPreviewPopoverOpen] = useState(false);
  const [isSelectDropdownOpen, setIsSelectDropdownOpen] = useState(false);
  const { setSelectedEmailId, selectedEmailId, selectedEmailSubject } =
    useSelectedEmailId();

  const parsedEmailPageTemplateId = emailPageTemplateId
    ? parseInt(emailPageTemplateId, 10)
    : -1;

  const isNew = emailPageTemplateId != null && emailPageTemplateId === 'new';
  const newTemplateString = useTranslation('new_template');

  const isReadOnly =
    isModuleFed &&
    emailTemplatePermissions != null &&
    !emailTemplatePermissions.canWrite;

  const companyFieldLookup = useMemo(
    () => keyBy(companyFields, 'fieldName'),
    [companyFields],
  );
  const meetingFieldLookup = useMemo(
    () => keyBy(meetingFields, 'fieldName'),
    [meetingFields],
  );

  const emailFieldLookup = useMemo(
    () => keyBy(emailFields, 'fieldName'),
    [emailFields],
  );

  const groupedLookupsByName: GroupFieldsMeta = useMemo(() => {
    let fieldsMeta = [
      {
        id: FilterGroupType.Company,
        fields: companyFieldLookup,
      },
      {
        id: FilterGroupType.Meeting,
        fields: meetingFieldLookup,
      },
      {
        id: FilterGroupType.Email,
        fields: emailFieldLookup,
      },
    ];

    return fieldsMeta;
  }, [companyFieldLookup, meetingFieldLookup, emailFieldLookup]);

  const groupedTransformer = useMemo(
    () =>
      getApiGroupedFilterTransformer(
        groupedLookupsByName,
        FilterGroupType.Company,
      ),
    [groupedLookupsByName],
  );

  const getDefaultData = useCallback(() => {
    return {
      templateId: parsedEmailPageTemplateId,
      sourceType: SourceType.Email as const,
      pageType: PageType.Email as const,
      templateName: newTemplateString,
      changedSections: defaultSections,
      tiptapJson: defaultContent,
      existingSectionsIds: DEFAULT_SECTION_IDS,
      htmlPayload: {},
      filters: {},
    };
  }, [newTemplateString, parsedEmailPageTemplateId]);
  const [templateData, setTemplateData] = useState<EmailPageTemplateUiState>();

  const { mutateAsync: createEmailPageTemplate, isPending: isSaving } =
    useCreateEmailPageTemplateMutation();

  const updateTemplateState = useCallback(
    (payload: Partial<EmailPageTemplateUiState>) => {
      setTemplateData((previous) =>
        previous != null
          ? { ...previous, ...payload }
          : { ...getDefaultData(), ...payload },
      );
    },
    [getDefaultData],
  );

  const {
    data: template,
    isLoading,
    isError: isErrorMeetingPageTemplate,
  } = useGetEmailPageTemplateQuery(parsedEmailPageTemplateId, {
    enabled: !isNew,
  });

  // Only consider it loading if the query is enabled (not in new mode)
  const isLoadingMeetingPageTemplate = isLoading && !isNew;

  const { mutateAsync: updateEmailPageTemplate, isPending: isUpdating } =
    useUpdateEmailPageTemplateMutation();

  useEffect(() => {
    if (
      isModuleFed &&
      emailTemplatePermissions != null &&
      !emailTemplatePermissions.canRead &&
      !isNew
    ) {
      navigate(
        `/${ROUTE_PATHS.TEMPLATES}/${ROUTE_PATHS.EMAIL_PAGE_TEMPLATE_LIST}`,
        { replace: true },
      );
    }
  }, [isModuleFed, emailTemplatePermissions, isNew, navigate]);

  useEffect(() => {
    if (isLoadingFields) {
      return;
    }

    if (isNew) {
      updateTemplateState(getDefaultData());
      setComponentKey((prev) => prev + 1);
    } else if (template && !isLoadingMeetingPageTemplate) {
      updateTemplateState({
        htmlPayload: template?.htmlPayload,
        tiptapJson: template?.tiptapJson,
        pageType: template?.pageType,
        sourceType: template?.sourceType,
        templateId: template?.templateId,
        templateName: template?.templateName,
        changedSections: [],
        existingSectionsIds: template?.templateSectionIds,
        filters: {
          pageTriggerCondition: template?.filters?.pageTriggerCondition
            ? groupedTransformer.from(
                template?.filters?.pageTriggerCondition as SimpleSearchFilter,
              )
            : undefined,
        },
      });
      setComponentKey((prev) => prev + 1);
    }
  }, [
    template,
    updateTemplateState,
    groupedTransformer,
    isLoadingFields,
    isNew,
    isLoadingMeetingPageTemplate,
    getDefaultData,
    newTemplateString,
  ]);

  const handleTitleChange = (newTitle: string) => {
    updateTemplateState({ templateName: newTitle });
  };

  const transformTemplateData = useCallback(
    (templateData: EmailPageTemplateUiState) => {
      const operations: OperationPayload[] =
        (templateData?.operations?.length ?? 0) > 0
          ? templateData.operations!
          : (() => {
              const built: OperationPayload[] = [];

              templateData?.changedSections?.forEach((section) => {
                if (isNew) {
                  built.push({ operation: 'POST', payload: section });
                } else if (
                  section?.attrs?.templateSectionId &&
                  templateData?.existingSectionsIds?.includes(
                    section?.attrs?.templateSectionId,
                  )
                ) {
                  built.push({ operation: 'PUT', payload: section });
                } else {
                  built.push({ operation: 'POST', payload: section });
                }
              });

              return built;
            })();

      const {
        changedSections: _changedSections,
        existingSectionsIds: _existingSectionsIds,
        operations: _operations,
        ...rest
      } = templateData;

      return {
        ...rest,
        operations,
        filters: {
          pageTriggerCondition: rest?.filters.pageTriggerCondition
            ? groupedTransformer.to(
                rest?.filters.pageTriggerCondition as GroupedFilter,
              )
            : undefined,
        },
      };
    },
    [isNew, groupedTransformer],
  );

  const handleTemplateSave = useCallback(async () => {
    try {
      if (templateData == null) {
        return;
      }
      if (isNew) {
        createEmailPageTemplate(transformTemplateData(templateData)).then(
          (response) => {
            const templateId = response?.templateId;

            updateTemplateState({
              templateId,
            });
            navigate(
              `/${ROUTE_PATHS.TEMPLATES}/${EMAIL_PAGE_ROUTE}/${templateId}`,
            );
          },
        );
      } else {
        updateEmailPageTemplate(transformTemplateData(templateData)).then(
          (response) => {
            updateTemplateState({
              ...response,
              operations: [],
              changedSections: [],
              filters: {
                pageTriggerCondition: response?.filters?.pageTriggerCondition
                  ? groupedTransformer.from(
                      response?.filters
                        ?.pageTriggerCondition as SimpleSearchFilter,
                    )
                  : undefined,
              },
            });
          },
        );
      }
    } catch (error) {
      logger.error('Error saving meeting page template', { error });
    }
  }, [
    isNew,
    createEmailPageTemplate,
    transformTemplateData,
    templateData,
    updateTemplateState,
    navigate,
    updateEmailPageTemplate,
    groupedTransformer,
  ]);

  return (
    <>
      {isLoadingMeetingPageTemplate || isLoadingFields ? (
        <PageLoading />
      ) : isErrorMeetingPageTemplate ? (
        <div>
          <FormattedMessage id="meeting_page_template.error.loading" />
        </div>
      ) : isPreviewOpen && templateData ? (
        <EmailPageLevelPreview
          isPreviewOpen={isPreviewOpen}
          setIsPreviewOpen={setIsPreviewOpen}
          templateData={templateData}
        />
      ) : templateData ? (
        <Argon
          key={componentKey}
          breadcrumbs={
            <>
              <Breadcrumb.Item>
                <Link
                  to={`/${ROUTE_PATHS.TEMPLATES}/${ROUTE_PATHS.EMAIL_PAGE_TEMPLATE_LIST}`}
                >
                  <FormattedMessage id="email_page_template" />
                </Link>
              </Breadcrumb.Item>
              <Breadcrumb.Item>&nbsp;</Breadcrumb.Item>
            </>
          }
          className="meeting-page-template-page"
          documentTitle={meetingPageTemplateTitle}
          title={
            <MeetingDocumentHeaderTitle
              value={templateData.templateName ?? ''}
              onChange={handleTitleChange}
              readonly={isReadOnly}
            />
          }
          actions={{}}
          pageActions={
            !isReadOnly ? (
              <FlexWrapperWithGutter>
                <Popover
                  open={isPreviewPopoverOpen}
                  onOpenChange={setIsPreviewPopoverOpen}
                  trigger="click"
                  placement="bottomLeft"
                  content={
                    <PreviewPopoverContent>
                      <PreviewPopoverHeader>
                        <PreviewPopoverTitle>
                          <FormattedMessage id="preview" />
                        </PreviewPopoverTitle>
                      </PreviewPopoverHeader>
                      <PreviewPopoverBody>
                        <PreviewPopoverLabel>
                          <FormattedMessage
                            id="preview.choose_email"
                            defaultMessage="Choose an email"
                          />
                        </PreviewPopoverLabel>
                        <Dropdown
                          open={isSelectDropdownOpen}
                          trigger={['click']}
                          placement="bottomLeft"
                          onOpenChange={(open) => setIsSelectDropdownOpen(open)}
                          overlay={
                            <PreviewDropdownMenu>
                              <EmailList
                                listWidth={510}
                                height={400}
                                isDropdownOpen={isSelectDropdownOpen}
                                isLoading={false}
                                selectedEmailId={selectedEmailId}
                                handleMenuClick={(
                                  emailId: number,
                                  email: Email,
                                ) => {
                                  setSelectedEmailId(emailId, email.subject);
                                  setIsSelectDropdownOpen(false);
                                }}
                                filter={
                                  templateData.filters?.pageTriggerCondition ??
                                  defaultGroupedFilter(FilterGroupType.Email)
                                }
                              />
                            </PreviewDropdownMenu>
                          }
                        >
                          <PreviewSelectTrigger
                            onClick={() => setIsSelectDropdownOpen(true)}
                          >
                            <div style={{ flex: 1 }}>
                              {selectedEmailSubject ? (
                                <PreviewSelectTriggerEllipsisText>
                                  {selectedEmailSubject}
                                </PreviewSelectTriggerEllipsisText>
                              ) : (
                                <span style={{ color: 'var(--text-tertiary)' }}>
                                  <FormattedMessage id="preview.choose" />
                                </span>
                              )}
                            </div>
                            <div style={{ cursor: 'pointer' }}>
                              <FontAwesomeIcon
                                icon={faChevronDown}
                                style={{ color: 'var(--text-tertiary)' }}
                              />
                            </div>
                          </PreviewSelectTrigger>
                        </Dropdown>
                      </PreviewPopoverBody>
                      <PreviewPopoverFooter>
                        <Button
                          variant="primary"
                          style={{
                            width: '100%',
                            justifyContent: 'center',
                            textAlign: 'center',
                          }}
                          onClick={() => {
                            setIsPreviewPopoverOpen(false);
                            setIsPreviewOpen(true);
                          }}
                          disabled={!selectedEmailId}
                        >
                          <FormattedMessage id="preview.preview_template" />
                        </Button>
                      </PreviewPopoverFooter>
                    </PreviewPopoverContent>
                  }
                >
                  <span>
                    <Tooltip title={<FormattedMessage id="preview" />}>
                      <PreviewButton
                        variant="secondary"
                        onClick={() => setIsPreviewPopoverOpen(true)}
                      >
                        <FontAwesomeIcon icon={faPlay} />
                      </PreviewButton>
                    </Tooltip>
                  </span>
                </Popover>
                <Button
                  variant="primary"
                  onClick={handleTemplateSave}
                  rightIcon={<FontAwesomeIcon icon={faFloppyDisk} />}
                  loading={isSaving || isUpdating}
                >
                  <FormattedMessage id="save" />
                </Button>
              </FlexWrapperWithGutter>
            ) : undefined
          }
          content={
            <EmailTemplatePage
              key={`template-${componentKey}`}
              templateData={templateData}
              onTemplateChange={updateTemplateState}
              groupedTransformer={groupedTransformer}
              previousTiptapJson={isNew ? undefined : template?.tiptapJson}
              isModuleFed={isModuleFed}
            />
          }
        />
      ) : (
        <></>
      )}
    </>
  );
};
